import React, { useState, useEffect, useCallback } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, MapPin, Globe, Clock, Users, Award,
  ChevronDown, ChevronUp, ExternalLink, Image as ImageIcon,
  ShieldCheck, Activity, Plane, CheckCircle, Stethoscope
} from 'lucide-react';
import QuoteRequestModal, { QuoteRequestButton } from '../../components/QuoteRequestModal';
import { VerifiedProvenanceTooltip } from '../../components/VerifiedProvenanceTooltip';
import { ConsentBackedIndicator } from '../../components/ConsentBackedIndicator';
import api from '../../utils/api';
import { useContent } from '../../hooks/useContent';
import SEO from '../../components/SEO';
import ClinicLogoBadge, { resolveClinicLogoUrl } from '../../components/ClinicLogoBadge';
import BeforeAfterProofChip from '../../components/BeforeAfterProofChip';
import ActiveCampaignsPanel from '../../components/ActiveCampaignsPanel';
import AccreditationEvidenceModal from '../../components/AccreditationEvidenceModal';
// TRUST-PROVENANCE-001 (2026-05-28) — title-attribute composer.
import { formatProvenanceTitle } from '../../utils/trustProvenance';

function WhyTrustSection({ badges, profile, accreditationDetails, onAccreditationClick }) {
  // TCPN-MP0-F cutover — `isVerified` derives EXCLUSIVELY from the
  // canonical 4-condition invariant on the patient-safe accreditation
  // projection. Legacy `badges.is_verified` is NO LONGER consulted
  // (charter §"REMOVE LEGACY TRUST SEMANTICS").
  //
  // OT-0F.1 reconciliation: the public profile endpoint redacts
  // `evidence.doc_id` per the public-evidence forbidden-list. The
  // backend has already filtered `verified_accreditations_detail[]` to
  // document-backed entries before redaction (see
  // `clinic_profile_enrichment_routes.py:496` — only `is_verified +
  // slug` rows pass through). The canonical FE predicate on this
  // surface is therefore `is_verified_new && !is_self_attested` PLUS
  // list membership; the `doc_id` field is intentionally absent and
  // its presence/absence MUST NOT be used as a trust signal here.
  const canonicalVerified = Array.isArray(accreditationDetails)
    ? accreditationDetails.filter(a =>
        a
        && a.slug
        && a.is_verified_new === true
        && a.is_self_attested !== true
      )
    : [];
  const isVerified = canonicalVerified.length > 0;
  // MP0-I.1B — section-visibility predicate reads ONLY canonical inputs.
  // `badges.verified_accreditations` (legacy list) no longer required.
  // The canonical accreditation source is `accreditationDetails`
  // (= `profile.verified_accreditations_detail`, OT-0F.1).
  const hasTrustData = badges && (
    badges.years_of_experience || badges.procedures_performed ||
    badges.international_patient_volume ||
    badges.trust_score != null
  );
  const hasDetailAccreditations = canonicalVerified.length > 0;

  if (!isVerified && !hasTrustData && !hasDetailAccreditations) return null;

  const metrics = [];
  if (badges?.years_of_experience) {
    metrics.push({ icon: Clock, value: `${badges.years_of_experience}+`, label: 'Years Experience' });
  }
  if (badges?.procedures_performed) {
    const formatted = badges.procedures_performed >= 1000
      ? `${(badges.procedures_performed / 1000).toFixed(0)},000+`
      : `${badges.procedures_performed}+`;
    metrics.push({ icon: Activity, value: formatted, label: 'Procedures Performed' });
  }
  if (badges?.international_patient_volume) {
    const formatted = badges.international_patient_volume >= 1000
      ? `${(badges.international_patient_volume / 1000).toFixed(0)},000+`
      : `${badges.international_patient_volume}+`;
    metrics.push({ icon: Plane, value: formatted, label: 'International Patients' });
  }

  // TCPN-MP0-F cutover — chip labels read EXCLUSIVELY from canonical
  // verified entries. Self-attested entries are intentionally excluded
  // from the profile top strip; they surface only in the detail panel
  // (charter §"PROFILE RULE").
  const accreditations = canonicalVerified
    .map(d => d.short_label || d.long_label || d.slug)
    .filter(Boolean);
  const speedLabels = { fast: 'Under 1 hour', quick: 'Under 6 hours', same_day: 'Same day' };
  const speedLabel = speedLabels[badges?.response_speed_category];
  const trustScore = badges?.trust_score;

  return (
    <Card className={`border shadow-sm ${isVerified ? 'border-emerald-200/60 bg-gradient-to-br from-emerald-50/40 to-white' : 'border-slate-200/60 bg-gradient-to-br from-slate-50/40 to-white'}`} data-testid="why-trust-section">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className={`h-5 w-5 ${isVerified ? 'text-emerald-600' : 'text-slate-400'}`} />
            <h2 className="text-sm font-semibold text-[#122033] font-[Manrope]">
              {isVerified ? 'Why Trust This Clinic' : 'Clinic Information'}
            </h2>
            {isVerified ? (
              <VerifiedProvenanceTooltip
                verificationStatus="verified"
                testId="verified-tooltip-public-profile"
                side="bottom"
              >
                <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px] cursor-help" data-testid="verified-badge">TreatX Verified</Badge>
              </VerifiedProvenanceTooltip>
            ) : (
              <Badge variant="outline" className="text-slate-500 border-slate-200 text-[10px]" data-testid="clinic-provided-badge">Provider Provided</Badge>
            )}
            {/* OTR-4: derived consent-backed B/A indicator. Renders only when
                at least one item in `treatment_showcase[].media.before_after`
                survived the backend's OTR-3 visibility predicate; otherwise
                null (silent empty state). No hidden / withdrawn / pending
                counts are ever exposed. */}
            <ConsentBackedIndicator profile={profile} />
          </div>
          {trustScore != null && (
            <div className="flex items-center gap-1.5" data-testid="trust-score-display">
              <div className={`text-lg font-bold ${trustScore >= 70 ? 'text-emerald-600' : trustScore >= 40 ? 'text-amber-600' : 'text-slate-500'}`}>
                {trustScore}
              </div>
              <span className="text-[10px] text-slate-400 leading-tight">Trust<br/>Score</span>
            </div>
          )}
        </div>

        {/* Metric grid */}
        {metrics.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4" data-testid="trust-metrics-grid">
            {metrics.map(({ icon: Icon, value, label }, i) => (
              <div key={i} className={`rounded-lg border p-3 text-center ${isVerified ? 'bg-white border-slate-100' : 'bg-slate-50/50 border-slate-100'}`} data-testid={`trust-metric-${i}`}>
                <Icon className={`h-4 w-4 mx-auto mb-1 ${isVerified ? 'text-emerald-500' : 'text-slate-400'}`} />
                <p className="text-lg font-bold text-[#122033]">{value}</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Accreditations */}
        {accreditations.length > 0 && (
          <div className="mb-3" data-testid="trust-accreditations">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5">
              {isVerified ? 'Verified Accreditations' : 'Accreditations'}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {accreditations.map((acc, i) => {
                // OT-0F.1 — clickable chip wired to the evidence modal.
                // Matches the chip label against the patient-safe
                // `verified_accreditations_detail` projection by slug
                // OR by short_label (label-cased).
                const detail = (accreditationDetails || []).find(d =>
                  d.slug === acc || d.short_label === acc || d.long_label === acc
                );
                const Chip = (
                  <Badge
                    key={i}
                    className={`text-[10px] gap-1 ${isVerified ? 'bg-emerald-50 text-emerald-700 border border-emerald-100 hover:bg-emerald-100' : 'bg-slate-50 text-slate-600 border border-slate-200'} ${onAccreditationClick && detail ? 'cursor-pointer transition-colors' : ''}`}
                    data-testid={`trust-accreditation-${i}`}
                    data-clickable={!!(onAccreditationClick && detail)}
                    data-render-authority="TrustBadgeCompact-v1"
                    data-render-generation="TRUST-001"
                    data-sprint-fingerprint="COM-FOUNDATION"
                    data-provenance-tooltip="TrustProvenanceTooltip-v1"
                    title={detail ? formatProvenanceTitle(detail) : (isVerified ? `${acc} — Verified by TreatX\nClick for evidence details` : `${acc} — Self-reported`)}
                    onClick={onAccreditationClick && detail ? () => onAccreditationClick(detail) : undefined}
                    role={onAccreditationClick && detail ? 'button' : undefined}
                    tabIndex={onAccreditationClick && detail ? 0 : undefined}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && onAccreditationClick && detail) {
                        e.preventDefault();
                        onAccreditationClick(detail);
                      }
                    }}
                  >
                    <Award className="h-3 w-3" /> {acc}
                    {isVerified && <ShieldCheck className="h-2.5 w-2.5 text-emerald-500 ml-0.5" />}
                  </Badge>
                );
                return Chip;
              })}
            </div>
          </div>
        )}

        {/* Response speed */}
        {speedLabel && (
          <div className="flex items-center gap-2 text-xs text-slate-600" data-testid="trust-response-speed">
            <Clock className={`h-3.5 w-3.5 ${isVerified ? 'text-emerald-500' : 'text-slate-400'}`} />
            <span>Average response time: <span className={`font-medium ${isVerified ? 'text-emerald-700' : 'text-slate-700'}`}>{speedLabel}</span></span>
          </div>
        )}

        {/* Verification source label */}
        {!isVerified && hasTrustData && (
          <p className="text-[10px] text-slate-400 mt-3 italic" data-testid="data-source-note">
            Data provided by clinic. Verification pending.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function InternationalSupportSection({ items }) {
  const [expanded, setExpanded] = React.useState(false);
  const visible = expanded ? items : items.slice(0, 2);
  return (
    <div data-testid="international-support">
      <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5">International Support</p>
      <div className="flex flex-wrap gap-1.5 items-center">
        {visible.map((item, i) => (
          <span key={i} className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-0.5">{item}</span>
        ))}
        {items.length > 2 && (
          <button onClick={() => setExpanded(!expanded)} className="text-[10px] text-violet-500 hover:text-violet-700 flex items-center gap-0.5" data-testid="support-show-more">
            {expanded ? <><ChevronUp className="h-3 w-3" />Show less</> : <><ChevronDown className="h-3 w-3" />+{items.length - 2} more</>}
          </button>
        )}
      </div>
    </div>
  );
}

function SocialIcon({ platform, url }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#3BA4C5] transition-colors" data-testid={`social-link-${platform}`}>
      <ExternalLink className="h-4 w-4" />
      <span className="sr-only">{platform}</span>
    </a>
  );
}

function BeforeAfterSection({ items }) {
  const [expanded, setExpanded] = useState(false);
  const { content: baDisclaimerContent } = useContent('ba_image_disclaimer');
  if (!items || items.length === 0) return null;

  return (
    <div data-testid="before-after-section">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 w-full text-left group"
        data-testid="ba-expand-toggle"
      >
        <p className="text-[10px] text-slate-400 uppercase tracking-wider">Before & After ({items.length})</p>
        {expanded
          ? <ChevronUp className="h-3 w-3 text-slate-400" />
          : <ChevronDown className="h-3 w-3 text-slate-400" />
        }
      </button>
      {expanded && (
        <div className="mt-2 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {items.map((ba, i) => (
              <div key={i} className="border border-slate-100 rounded-lg p-3 space-y-2" data-testid={`ba-card-${i}`}>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-slate-100 rounded-md h-20 flex items-center justify-center">
                    <ImageIcon className="h-5 w-5 text-slate-300" />
                    <span className="text-[9px] text-slate-400 ml-1">Before</span>
                  </div>
                  <div className="bg-slate-100 rounded-md h-20 flex items-center justify-center">
                    <ImageIcon className="h-5 w-5 text-slate-300" />
                    <span className="text-[9px] text-slate-400 ml-1">After</span>
                  </div>
                </div>
                {ba.caption && <p className="text-xs text-slate-500">{ba.caption}</p>}
                <p className="text-[9px] text-slate-400 italic leading-relaxed" data-testid={`ba-disclaimer-${i}`}>
                  {ba.disclaimer || baDisclaimerContent?.body || ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * B2D.5 — SSOT-driven Before/After section.
 * Reads from the public B2D endpoint:
 *   GET /api/public/clinics/{clinicId}/before-after-cases?treatment_slug=...
 * Renders ONLY approved + consent-valid cases (server-enforced).
 * No PII, no metadata leak, no engagement metrics, no social mechanics.
 * ─────────────────────────────────────────────────────────────────── */
function BeforeAfterCasesSSOT({ clinicId, treatmentSlug }) {
  const { content: baDisclaimerContent } = useContent('ba_image_disclaimer');
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!clinicId) return;
    let alive = true;
    setLoading(true);
    const url = treatmentSlug
      ? `/public/clinics/${clinicId}/before-after-cases?treatment_slug=${encodeURIComponent(treatmentSlug)}`
      : `/public/clinics/${clinicId}/before-after-cases`;
    api.get(url)
      .then((r) => { if (alive) setCases(r.data?.cases || []); })
      .catch(() => { if (alive) setCases([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [clinicId, treatmentSlug]);

  if (loading || cases.length === 0) return null;

  return (
    <div data-testid="before-after-cases-ssot">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 w-full text-left group"
        data-testid="ba-ssot-toggle"
      >
        <p className="text-[10px] text-slate-400 uppercase tracking-wider">
          Approved before/after cases ({cases.length})
        </p>
        {expanded
          ? <ChevronUp className="h-3 w-3 text-slate-400" />
          : <ChevronDown className="h-3 w-3 text-slate-400" />
        }
      </button>
      {expanded && (
        <div className="mt-2 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {cases.map((c, i) => {
              // TREATX-BA-KILL-SWITCH-01 / TREATX-BA-MEDIA-ENDPOINT-HARDENING-01:
              // when the backend returns visibility_status === "blocked"
              // for a case, render a neutral placeholder card instead of
              // attempting to load the original image URL.
              const isBlocked = c.visibility_status === 'blocked';
              const placeholderText = c.placeholder_text
                || 'This medical image is currently unavailable due to consent or compliance settings.';
              return (
              <div key={c.case_id || i} className="border border-slate-100 rounded-lg p-3 space-y-2" data-testid={`ba-ssot-card-${c.case_id || i}`}>
                <div className="grid grid-cols-2 gap-2">
                  {isBlocked ? (
                    <>
                      <div className="bg-slate-50 rounded-md aspect-square overflow-hidden flex items-center justify-center p-2 border border-dashed border-slate-200"
                           data-testid={`ba-ssot-placeholder-before-${i}`}>
                        <span className="text-[10px] text-slate-500 text-center leading-snug">
                          {placeholderText}
                        </span>
                      </div>
                      <div className="bg-slate-50 rounded-md aspect-square overflow-hidden flex items-center justify-center p-2 border border-dashed border-slate-200"
                           data-testid={`ba-ssot-placeholder-after-${i}`}>
                        <span className="text-[10px] text-slate-500 text-center leading-snug">
                          {placeholderText}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bg-slate-100 rounded-md aspect-square overflow-hidden flex items-center justify-center">
                        <img
                          src={`${backendUrl}${c.before_thumbnail_url}`}
                          alt="Before"
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </div>
                      <div className="bg-slate-100 rounded-md aspect-square overflow-hidden flex items-center justify-center">
                        <img
                          src={`${backendUrl}${c.after_thumbnail_url}`}
                          alt="After"
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </div>
                    </>
                  )}
                </div>
                {c.caption && <p className="text-xs text-slate-600">{c.caption}</p>}
                <p className="text-[9px] text-slate-400 italic leading-relaxed" data-testid={`ba-ssot-disclaimer-${i}`}>
                  {baDisclaimerContent?.body || 'Individual results vary. Reviewed by TreatX.'}
                </p>
              </div>
            );})}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PublicClinicProfile() {
  const { clinicId } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const navState = routerLocation.state || {};
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState({});
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  // OT-0F.1 — campaign-driven quote requests carry their campaign_id forward
  // so the existing C4A snapshot pipeline can attribute the lead.
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  // OT-0F.1 — accreditation evidence drilldown.
  const [accreditationModal, setAccreditationModal] = useState(null);

  const handleCampaignRequest = (camp) => {
    if (!camp) return;
    setSelectedCampaignId(camp.campaign_id || null);
    if (camp.treatment_type) setPreselectedTreatment(camp.treatment_type);
    setShowQuoteModal(true);
  };

  // Treatment selection persistence: read from multiple sources (priority order)
  // 1. URL search param ?treatment=slug (from hash routing)
  // 2. Router state from FindClinicsPage navigation
  // 3. Empty fallback
  // Initialize with URL value immediately to avoid flash of empty state
  const getUrlTreatment = () => {
    const hash = window.location.hash || '';
    const queryPart = hash.split('?')[1] || '';
    const qp = new URLSearchParams(queryPart);
    // P0-X2.E3-A — also accept canonical `treatment_type` param for
    // deep-links / shared URLs that use the backend-canonical name.
    return qp.get('treatment') || qp.get('treatment_type') || '';
  };
  
  const [preselectedTreatment, setPreselectedTreatment] = useState(() => {
    const urlTreatment = getUrlTreatment();
    return urlTreatment || navState?.preselectedTreatment || '';
  });

  // Update preselectedTreatment when navigation state OR URL hash changes
  // (P0-X2.E3-A — listen to routerLocation so refetch triggers when only
  // the `?treatment_type=` query string changes without a clinicId change).
  useEffect(() => {
    const urlTreatment = getUrlTreatment();
    const stateTreatment = navState?.preselectedTreatment || '';
    const newPreselected = urlTreatment || stateTreatment || '';
    if (newPreselected !== preselectedTreatment) {
      setPreselectedTreatment(newPreselected);
    }
  }, [clinicId, navState?.preselectedTreatment, routerLocation.key, routerLocation.search, routerLocation.hash]);

  const fetchProfile = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // P0-X2.E3-A — forward the page-level treatment context to the
      // backend so the treatment-aware campaign overlay can trigger.
      // The backend has an independent fallback that returns the
      // provider's most-recent active campaign when no treatment_type
      // is supplied, so this query param is purely additive.
      const tt = getUrlTreatment();
      const qs = tt ? `?treatment_type=${encodeURIComponent(tt)}` : '';
      const { data } = await api.get(`/public/clinics/${clinicId}/profile${qs}`);
      setProfile(data);
      const treatments = Object.keys(data.treatment_showcase || {});
      const realTrts = treatments.filter(t => !t.toLowerCase().startsWith('test'));
      if (realTrts.length > 0) setActiveTab(realTrts[0]);
      else if (treatments.length > 0) setActiveTab(treatments[0]);
    } catch (e) {
      if (e.response?.status === 404) setError('Clinic not found');
      else setError(e.message);
    }
    finally { setLoading(false); }
  }, [clinicId, preselectedTreatment]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  // PRD1.9 Phase 1: Fetch doctors from provider_affiliations via providers endpoint (SSOT)
  const [realDoctors, setRealDoctors] = useState([]);
  useEffect(() => {
    if (!clinicId) return;
    api.get(`/providers/${clinicId}/doctors`)
      .then(res => setRealDoctors((res.data?.doctors || []).filter(d => d.status === 'active')))
      .catch(() => setRealDoctors([]));
  }, [clinicId]);

  // PRD1.7-4B: Fetch verified_fields for badge display
  const [verifiedFields, setVerifiedFields] = useState({});
  useEffect(() => {
    if (!clinicId) return;
    api.get(`/public/clinics/${clinicId}/verified-fields`)
      .then(res => setVerifiedFields(res.data?.verified_fields || {}))
      .catch(() => setVerifiedFields({}));
  }, [clinicId]);

  // Auto-open quote modal when navigated with requestQuote state
  useEffect(() => {
    if (navState?.requestQuote && profile && !showQuoteModal) {
      setShowQuoteModal(true);
    }
  }, [navState?.requestQuote, profile]);

  if (loading) return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="public-profile-loading">
      <SEO title="Loading provider…" canonicalPath={`/clinics/${clinicId}`} />
      <div className="space-y-3 w-full max-w-2xl px-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-slate-100 animate-pulse rounded-lg" />)}</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="public-profile-error">
      {/* PUBLIC-SEO-LAUNCH-GAP-REMEDIATION: 404 from
          `/api/public/clinics/{clinic_id}/profile` renders a proper
          Not Found UI with robots=noindex,follow. The backend API
          already emits HTTP 404 for unknown clinic_ids (see
          backend/routes/clinic_profile_enrichment_routes.py). */}
      <SEO
        title={error === 'Clinic not found' ? 'Provider Not Found' : 'Provider Unavailable'}
        description={error === 'Clinic not found' ? 'The provider you are looking for does not exist or is no longer listed on TreatX.' : 'This provider profile is temporarily unavailable.'}
        noindex
        statusCode={error === 'Clinic not found' ? 404 : 503}
      />
      <Card className="w-96"><CardContent className="p-6 text-center">
        <p className="text-sm text-slate-500 mb-3" data-testid="public-profile-error-message">{error}</p>
        <Button onClick={() => navigate('/')} data-testid="go-home">Go Home</Button>
      </CardContent></Card>
    </div>
  );

  if (!profile) return null;

  const about = profile.about || {};
  const social = profile.social_links || {};
  const showcase = profile.treatment_showcase || {};
  const treatments = Object.keys(showcase);
  const currentTreatment = showcase[activeTab] || {};
  const beforeAfter = currentTreatment.media?.before_after || [];
  const campaign = profile.active_campaign;
  const doctors = realDoctors.length > 0 ? realDoctors : (profile.doctors || profile.team || []);
  const certifications = profile.certifications || about.accreditations || [];
  // P1-4: Filter out test treatments from showcase
  const realTreatments = treatments.filter(t => !t.toLowerCase().startsWith('test'));

  // TREATX-SEO-RECOVERY-S3B — `profile.logo` is a MEDIA DESCRIPTOR object
  // ({media_id, thumbnail_url, preview_url} | null) as returned by
  // /api/public/clinics/{id}/profile, NOT a URL. It used to be handed straight
  // to <SEO image>, where absolutise() called .startsWith on an object, threw,
  // and unmounted the entire page — an existing provider rendered blank while
  // an unknown one rendered correctly. Resolved here through the canonical
  // logo resolver that ClinicLogoBadge already owns; `preview` is the larger
  // variant, which is the right one for a social card.
  const logoUrl = resolveClinicLogoUrl(profile.logo, 'preview') || undefined;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="public-clinic-profile">
      {/* PUBLIC-SEO-LAUNCH-GAP-REMEDIATION: canonical provider
          metadata derived from clinic_profiles SSOT (via the
          /public/clinics/{clinic_id}/profile response). Zero
          fabrication: description falls back to a neutral platform
          copy only when no clinic-authored tagline/about is present. */}
      <SEO
        title={profile.clinic_name || 'Provider'}
        description={
          about.tagline ||
          (about.long_description ? String(about.long_description).slice(0, 200) : `${profile.clinic_name || 'Verified provider'} on TreatX — compare treatments, credentials and pricing.`)
        }
        canonicalPath={`/clinics/${clinicId}`}
        image={logoUrl}
        imageAlt={profile.clinic_name || undefined}
        type="profile"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'MedicalOrganization',
          name: profile.clinic_name || clinicId,
          url: `https://treatx.health/clinics/${clinicId}`,
          logo: logoUrl,
          description: about.long_description || about.tagline || undefined,
          address: about.address_display || about.city || about.country
            ? {
                '@type': 'PostalAddress',
                addressLocality: about.city || undefined,
                addressCountry: about.country || undefined,
              }
            : undefined,
        }}
      />
      {/* Hero */}
      <div className="bg-[#122033] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/10 mb-4" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          {/* TCPN S1-C2: compact provider logo (or initials fallback) — left of name */}
          <div className="flex items-start gap-3">
            <ClinicLogoBadge
              logo={profile.logo}
              name={profile.clinic_name || clinicId}
              size="md"
              testid="clinic-logo-profile"
            />
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold font-[Manrope] tracking-tight" data-testid="clinic-name">
                {profile.clinic_name || clinicId}
              </h1>
              {about.tagline && <p className="text-white/70 mt-2 text-base" data-testid="clinic-tagline">{about.tagline}</p>}
              {!about.tagline && about.long_description && (
                <p className="text-white/60 mt-2 text-sm line-clamp-2">{about.long_description}</p>
              )}
              {/* TCPN S1-E: consent-backed BA proof chip on profile hero */}
              {profile.has_before_after_proof && (
                <div className="mt-3" data-testid="profile-trust-chips">
                  <BeforeAfterProofChip
                    clinicId={clinicId}
                    clinicName={profile.clinic_name || clinicId}
                    testid="ba-proof-profile"
                  />
                </div>
              )}
              {/* TCPN S2-C: trip-mode trust pill on profile hero
                  (hidden for treatment_only per the contract). */}
              {profile.trip_mode && profile.trip_mode !== 'treatment_only' && (
                <div className="mt-2">
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-sky-500/20 text-sky-100 border border-sky-300/40"
                    data-testid="profile-trip-mode-pill"
                    data-trip-mode={profile.trip_mode}
                    title={
                      profile.trip_mode === 'vip_full_support'
                        ? 'This clinic provides VIP-style coordination and premium travel support.'
                        : 'This clinic offers travel-support services such as transfer or accommodation assistance.'
                    }
                  >
                    {profile.trip_mode === 'vip_full_support' ? 'VIP full support' : 'Treatment + travel'}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-4">
            {(profile.city || profile.country) && (
              <Badge variant="outline" className="text-white/60 border-white/20 text-xs gap-1">
                <MapPin className="h-3 w-3" />{[profile.city, profile.country].filter(Boolean).join(', ')}
              </Badge>
            )}
            {about.years_in_service != null && verifiedFields.years_of_experience?.status !== 'rejected' && (
              <Badge variant="outline" className="text-white/60 border-white/20 text-xs gap-1">
                <Clock className="h-3 w-3" />{about.years_in_service} years
                {verifiedFields.years_of_experience?.status === 'verified' && <CheckCircle className="h-3 w-3 text-emerald-400" />}
              </Badge>
            )}
            {about.doctors_count != null && verifiedFields.doctors_count?.status !== 'rejected' && (
              <Badge variant="outline" className="text-white/60 border-white/20 text-xs gap-1">
                <Users className="h-3 w-3" />{about.doctors_count} doctors
                {verifiedFields.doctors_count?.status === 'verified' && <CheckCircle className="h-3 w-3 text-emerald-400" />}
              </Badge>
            )}
            {campaign && (
              <Badge
                className="bg-emerald-500 text-white text-xs gap-1 cursor-pointer hover:bg-emerald-600 transition-colors"
                data-testid="campaign-badge"
                onClick={() => {
                  const el = document.getElementById('campaigns-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const el = document.getElementById('campaigns-section');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
                title="Scroll to International Offers"
              >
                {campaign.short_badge_text || campaign.title}
              </Badge>
            )}
          </div>
          {/* Social */}
          {Object.keys(social).length > 0 && (
            <div className="flex gap-3 mt-4" data-testid="social-links">
              {Object.entries(social).map(([k, v]) => v && <SocialIcon key={k} platform={k} url={v} />)}
            </div>
          )}
          {/* P0-3: Quote Request CTA — prominent, above the fold */}
          <div className="mt-5" data-testid="profile-quote-cta">
            <QuoteRequestButton clinicId={clinicId} clinicName={profile.clinic_name || clinicId} preselectedTreatment={preselectedTreatment} />
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        {/* P0-3: Always show description — fallback if none exists */}
        {about.long_description ? (
          <p className="text-sm text-slate-600 leading-relaxed" data-testid="long-description">{about.long_description}</p>
        ) : (
          <p className="text-sm text-slate-500 italic" data-testid="description-placeholder">
            {profile.clinic_name || 'This clinic'} offers professional medical treatments. Request a quote to learn more about their services.
          </p>
        )}

        {/* OT-0F.1 — Patient-side Active Campaigns & Offers panel.
            Renders all active campaigns; clinic CTA flow handles the
            campaign_id handoff into the existing C4A snapshot. */}
        <ActiveCampaignsPanel
          campaigns={profile.active_campaigns}
          clinicId={clinicId}
          clinicName={profile.clinic_name || clinicId}
          onRequest={handleCampaignRequest}
        />

        {/* Why Trust This Clinic — verified trust section */}
        <WhyTrustSection
          badges={profile.trust_badges}
          profile={profile}
          accreditationDetails={profile.verified_accreditations_detail}
          onAccreditationClick={(a) => setAccreditationModal(a)}
        />

        {/* OF-006 — Facility / interior / building photos carousel.
            Canonical reader: profile.facility_gallery (cover-first,
            order_index asc). Each item is approved + published in
            media_assets with context_type='clinic_profile'.
            PDP-002: graceful fallback when the underlying media row
            is malformed (e.g. seed/test data with width<100), so the
            patient never sees a dead-black tile. Renders a styled
            placeholder with an icon + tile index. */}
        {Array.isArray(profile.facility_gallery) && profile.facility_gallery.length > 0 && (
          <div className="space-y-2" data-testid="facility-gallery">
            <h3 className="text-sm font-semibold text-[#122033]">Facility</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {profile.facility_gallery.map((m, idx) => {
                const malformed = (Number(m.width) > 0 && Number(m.width) < 100) ||
                                  (Number(m.height) > 0 && Number(m.height) < 100);
                return (
                  <a
                    key={m.media_id}
                    href={malformed ? '#' : m.preview_url}
                    onClick={malformed ? ((e) => e.preventDefault()) : undefined}
                    target={malformed ? undefined : '_blank'}
                    rel={malformed ? undefined : 'noopener noreferrer'}
                    className={`aspect-video rounded-md overflow-hidden bg-slate-100 block relative ${m.is_cover ? 'ring-2 ring-emerald-500 col-span-2 row-span-2 sm:col-span-2 lg:col-span-2' : ''}`}
                    data-testid={`facility-gallery-item-${idx}`}
                    data-cover={m.is_cover ? 'true' : 'false'}
                    data-malformed={malformed ? 'true' : 'false'}
                  >
                    {malformed ? (
                      <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex flex-col items-center justify-center text-slate-400">
                        <ImageIcon className="h-6 w-6 mb-1 opacity-60" />
                        <span className="text-[10px] uppercase tracking-wider">Facility photo</span>
                      </div>
                    ) : (
                      <img
                        src={m.thumbnail_url}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    )}
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Differentiators */}
        {about.differentiators?.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="differentiators">
            {about.differentiators.map((d, i) => <Badge key={i} variant="outline" className="text-xs border-[#3BA4C5]/30 text-[#3BA4C5]">{d}</Badge>)}
          </div>
        )}

        {/* Languages */}
        {about.languages?.length > 0 && (
          <div className="flex items-center gap-2" data-testid="languages">
            <Globe className="h-3.5 w-3.5 text-slate-400" />
            <span className="text-xs text-slate-500">{about.languages.join(', ')}</span>
          </div>
        )}

        {/* TCPN MP0-F cutover — render ONLY entries that pass the
            canonical invariant on this public-profile surface:
              is_verified_new === true && is_self_attested !== true
            Plus list membership: the backend has already filtered
            `verified_accreditations_detail[]` to document-backed
            entries before applying OT-0F.1 redaction, so the chip
            predicate intentionally omits the `evidence.doc_id` check
            (which is on the public-evidence forbidden-list on this
            endpoint). Self-attested entries are intentionally excluded
            here — they surface in the detail-panel split. */}
        {Array.isArray(profile.verified_accreditations_detail) && profile.verified_accreditations_detail.length > 0 && (() => {
          const chips = profile.verified_accreditations_detail.filter((a) =>
            a
            && a.slug
            && a.is_verified_new === true
            && a.is_self_attested !== true
          );
          if (!chips.length) return null;
          return (
            <div className="flex flex-wrap gap-2" data-testid="accreditations">
              {chips.map((a, i) => (
                <Badge
                  key={a.slug || i}
                  className="text-[10px] gap-1 bg-emerald-100 text-emerald-800 border border-emerald-300 cursor-help"
                  title={a.long_label || a.short_label || ''}
                  data-testid={`accreditation-${a.slug || i}`}
                  data-accreditation-slug={a.slug || ''}
                >
                  <Award className="h-3 w-3" />{a.short_label || a.slug}
                  <CheckCircle className="h-3 w-3 ml-0.5 text-emerald-600" />
                </Badge>
              ))}
            </div>
          );
        })()}

        {/* TCPN S2-F — Verified specialties section (master-validated only).
            Source: profile.verified_specialties_detail (PCS-projected via
            specialty_master + verified-doctor affiliation chain). Each row
            shows the canonical specialty name and the count of verified
            doctors on staff. Read-only — no click-through, no doctor PII,
            no free-text leakage. */}
        {Array.isArray(profile.verified_specialties_detail) && profile.verified_specialties_detail.length > 0 && (
          <div className="flex flex-col gap-1.5" data-testid="verified-specialties">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Verified specialties</p>
            <div className="flex flex-wrap gap-2">
              {profile.verified_specialties_detail.map((s) => (
                <Badge
                  key={s.slug}
                  className="text-[10px] gap-1 bg-indigo-50 text-indigo-700 border border-indigo-200"
                  data-testid={`specialty-${s.slug}`}
                  data-specialty-slug={s.slug}
                >
                  <Stethoscope className="h-3 w-3" />
                  {s.name}
                  <span className="ml-1 text-indigo-500">
                    ({s.verified_doctor_count} verified {s.verified_doctor_count === 1 ? 'doctor' : 'doctors'})
                  </span>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* D1: Experience Model Badge */}
        {profile.experience_model && profile.experience_model !== 'STANDARD' && (
          <Badge variant="outline" className="text-[10px] border-violet-200 text-violet-600 w-fit" data-testid="experience-model-badge">
            {profile.experience_model === 'BOUTIQUE' ? 'Boutique Clinic' : 'High Volume Expert'}
          </Badge>
        )}

        {/* D1: Positioning Tags */}
        {profile.positioning_tags?.length > 0 && (
          <div data-testid="positioning-tags">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5">Why choose us</p>
            <div className="flex flex-wrap gap-1.5">
              {profile.positioning_tags.map((tag, i) => (
                <Badge key={i} variant="outline" className="text-[10px] border-violet-200 text-violet-600 bg-violet-50/50">{tag}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* D1: International Support */}
        {profile.international_support?.length > 0 && (
          <InternationalSupportSection items={profile.international_support} />
        )}

        {/* P1-1: Doctor/Team Info — PRD1.7-4A: real data from doctors API */}
        {doctors.length > 0 ? (
          <Card className="border border-slate-200/60 shadow-sm" data-testid="doctors-section">
            <CardContent className="p-5">
              <h2 className="text-sm font-semibold text-[#122033] font-[Manrope] mb-3 flex items-center gap-2">
                <Users className="h-4 w-4 text-slate-400" /> Medical Team ({doctors.length})
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {doctors.map((doc, i) => {
                  const docName = doc.full_name || doc.name || doc.doctor_name || 'Doctor';
                  const docSpecialty = (doc.specializations || [])[0] || doc.specialty || doc.title || '';
                  const docExperience = doc.years_of_experience || doc.experience_years || 0;
                  return (
                    <div key={doc.doctor_id || i} className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 bg-white" data-testid={`doctor-card-${doc.doctor_id || i}`}>
                      {doc.photo ? (
                        <img src={doc.photo} alt={docName} className="w-10 h-10 rounded-full object-cover shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                          <Users className="h-5 w-5 text-slate-400" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#122033]">{docName}</p>
                        {docSpecialty && <p className="text-xs text-slate-500">{docSpecialty}</p>}
                        {docExperience > 0 && <p className="text-[10px] text-slate-400">{docExperience}+ years experience</p>}
                        {doc.total_operations > 0 && <p className="text-[10px] text-slate-400">{doc.total_operations.toLocaleString()} procedures</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : about.doctors_count ? (
          <Card className="border border-slate-200/60 shadow-sm" data-testid="doctors-section-fallback">
            <CardContent className="p-5">
              <h2 className="text-sm font-semibold text-[#122033] font-[Manrope] mb-2 flex items-center gap-2">
                <Users className="h-4 w-4 text-slate-400" /> Medical Team
              </h2>
              <p className="text-xs text-slate-500">{about.doctors_count} qualified doctors on staff. Request a quote to learn more about the team.</p>
            </CardContent>
          </Card>
        ) : null}

        {/* P1-1: Treatments Offered — always visible listing */}
        {realTreatments.length > 0 && (
          <Card className="border border-slate-200/60 shadow-sm" data-testid="treatments-list">
            <CardContent className="p-5">
              <h2 className="text-sm font-semibold text-[#122033] font-[Manrope] mb-3">Treatments Offered</h2>
              <div className="flex flex-wrap gap-2">
                {realTreatments.map(t => (
                  <Badge key={t} variant="outline" className="text-xs border-teal-200 text-teal-700 bg-teal-50/50 cursor-pointer hover:bg-teal-100" onClick={() => setActiveTab(t)}>
                    {t.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* TCPN S2-C — Travel & Concierge support (VIP services list).
            Read-only chip list. Per the S2-C contract: NEVER turn raw VIP
            services into searchable filters; the only filter exposed in
            search is the canonical `trip_mode` enum. */}
        {Array.isArray(profile.vip_services) && profile.vip_services.length > 0 && (
          <Card className="border border-slate-200/60 shadow-sm" data-testid="vip-services-list">
            <CardContent className="p-5">
              <h2 className="text-sm font-semibold text-[#122033] font-[Manrope] mb-3">
                Travel &amp; concierge support
              </h2>
              <div className="flex flex-wrap gap-2">
                {profile.vip_services.map((slug) => (
                  <Badge
                    key={slug}
                    variant="outline"
                    className="text-xs border-sky-200 text-sky-700 bg-sky-50/50"
                    data-testid={`vip-service-chip-${slug}`}
                  >
                    {slug.replace(/-/g, ' ')}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* TCPN S2-D — Languages spoken (provider-level chip list).
            Read-only. Doctor-level languages are NOT mixed into this view.
            Full list (no 3-chip limit, public profile only). */}
        {Array.isArray(profile.languages) && profile.languages.length > 0 && (
          <Card className="border border-slate-200/60 shadow-sm" data-testid="languages-list">
            <CardContent className="p-5">
              <h2 className="text-sm font-semibold text-[#122033] font-[Manrope] mb-3">
                Languages spoken
              </h2>
              <div className="flex flex-wrap gap-2">
                {profile.languages.map((slug) => (
                  <Badge
                    key={slug}
                    variant="outline"
                    className="text-xs border-slate-200 text-slate-700 bg-slate-50/60"
                    data-testid={`language-chip-${slug}`}
                  >
                    {slug.charAt(0).toUpperCase() + slug.slice(1)}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Treatment Tabs */}
        {realTreatments.length > 0 && (
          <div data-testid="treatment-tabs">
            <div className="flex gap-1 border-b border-slate-100 mb-4">
              {realTreatments.map(t => (
                <button key={t} className={`px-3 py-2 text-xs font-medium transition-colors ${activeTab === t ? 'border-b-2 border-[#3BA4C5] text-[#3BA4C5]' : 'text-slate-400 hover:text-slate-600'}`}
                  onClick={() => setActiveTab(t)} data-testid={`tab-${t}`}>
                  {t.replace(/_/g, ' ')}
                </button>
              ))}
            </div>

            {activeTab && (
              <Card className="border border-slate-200/60 shadow-sm" data-testid="treatment-content">
                <CardContent className="p-5 space-y-4">
                  {currentTreatment.headline && <h3 className="text-base font-semibold text-[#122033] font-[Manrope]" data-testid="treatment-headline">{currentTreatment.headline}</h3>}

                  {/* P0-X2.E3-B — Treatment Context block.  Renders ONLY when
                      canonical fields are present (clinic_treatments override
                      OR master catalog enrichment).  No synthesized copy.
                      No envelope fallback.  Absent values are silently hidden. */}
                  {(currentTreatment.price_min != null ||
                    currentTreatment.procedure_duration ||
                    currentTreatment.hospital_stay ||
                    currentTreatment.recovery_time ||
                    currentTreatment.short_description ||
                    currentTreatment.master_short_description) && (
                    <div
                      className="rounded-lg border border-slate-200/70 bg-slate-50/60 p-4 space-y-3"
                      data-testid="treatment-context-block"
                      data-treatment-slug={activeTab}
                    >
                      {(currentTreatment.short_description || currentTreatment.master_short_description) && (
                        <p
                          className="text-xs text-slate-600 leading-relaxed"
                          data-testid="treatment-context-summary"
                          data-source={currentTreatment.short_description ? 'provider' : 'master'}
                        >
                          {currentTreatment.short_description || currentTreatment.master_short_description}
                        </p>
                      )}

                      {(currentTreatment.price_min != null || currentTreatment.price_max != null) && (
                        <div className="flex flex-wrap items-baseline gap-2" data-testid="treatment-context-price">
                          <span className="text-[10px] uppercase tracking-wider text-slate-400">Price from</span>
                          <span className="text-sm font-semibold text-[#122033]">
                            {currentTreatment.price_min != null ? `${currentTreatment.currency || 'EUR'} ${currentTreatment.price_min.toLocaleString()}` : ''}
                            {currentTreatment.price_max != null && currentTreatment.price_min != null && ` – ${currentTreatment.price_max.toLocaleString()}`}
                            {currentTreatment.price_max != null && currentTreatment.price_min == null && `${currentTreatment.currency || 'EUR'} ${currentTreatment.price_max.toLocaleString()}`}
                          </span>
                        </div>
                      )}

                      {(currentTreatment.procedure_duration || currentTreatment.hospital_stay || currentTreatment.recovery_time) && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1" data-testid="treatment-context-durations">
                          {currentTreatment.procedure_duration && (
                            <div className="flex items-center gap-2" data-testid="treatment-context-procedure-duration">
                              <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                              <div className="leading-tight">
                                <p className="text-[10px] uppercase tracking-wider text-slate-400">Procedure</p>
                                <p className="text-xs text-slate-700">{currentTreatment.procedure_duration}</p>
                              </div>
                            </div>
                          )}
                          {currentTreatment.hospital_stay && (
                            <div className="flex items-center gap-2" data-testid="treatment-context-hospital-stay">
                              <Activity className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                              <div className="leading-tight">
                                <p className="text-[10px] uppercase tracking-wider text-slate-400">Hospital stay</p>
                                <p className="text-xs text-slate-700">{currentTreatment.hospital_stay}</p>
                              </div>
                            </div>
                          )}
                          {currentTreatment.recovery_time && (
                            <div className="flex items-center gap-2" data-testid="treatment-context-recovery">
                              <ShieldCheck className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                              <div className="leading-tight">
                                <p className="text-[10px] uppercase tracking-wider text-slate-400">Recovery</p>
                                <p className="text-xs text-slate-700">{currentTreatment.recovery_time}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* P0-X2.E3-B — Campaign Detail block.  Renders ONLY when the
                      active campaign is bound to the currently selected
                      treatment tab.  All fields are conditional; absent fields
                      are hidden.  No fabricated text. */}
                  {campaign && campaign.treatment_type === activeTab && (
                    <div
                      className="rounded-lg border border-emerald-200/70 bg-emerald-50/50 p-4 space-y-2"
                      data-testid="campaign-detail-block"
                      data-campaign-id={campaign.campaign_id}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        {campaign.short_badge_text && (
                          <Badge className="bg-emerald-500 text-white text-[10px]" data-testid="campaign-detail-badge">
                            {campaign.short_badge_text}
                          </Badge>
                        )}
                        {campaign.title && (
                          <span className="text-sm font-semibold text-emerald-900" data-testid="campaign-detail-title">
                            {campaign.title}
                          </span>
                        )}
                      </div>
                      {campaign.description && (
                        <p className="text-xs text-emerald-900/80 leading-relaxed" data-testid="campaign-detail-description">
                          {campaign.description}
                        </p>
                      )}
                      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs">
                        {campaign.discount_value != null && campaign.discount_value !== 0 && (
                          <span className="text-emerald-800" data-testid="campaign-detail-discount">
                            <span className="text-[10px] uppercase tracking-wider text-emerald-700/70 mr-1">Discount</span>
                            {campaign.discount_type === 'percentage' ? `${campaign.discount_value}%` : `${campaign.currency || 'EUR'} ${campaign.discount_value}`}
                          </span>
                        )}
                        {(campaign.original_price_hint != null || campaign.campaign_price_hint != null) && (
                          <span className="text-emerald-800" data-testid="campaign-detail-price-hint">
                            <span className="text-[10px] uppercase tracking-wider text-emerald-700/70 mr-1">Price</span>
                            {campaign.original_price_hint != null && (
                              <span className="line-through text-emerald-700/60 mr-1">
                                {campaign.currency || 'EUR'} {campaign.original_price_hint.toLocaleString()}
                              </span>
                            )}
                            {campaign.campaign_price_hint != null && (
                              <span className="font-semibold">
                                {campaign.currency || 'EUR'} {campaign.campaign_price_hint.toLocaleString()}
                              </span>
                            )}
                          </span>
                        )}
                        {campaign.end_at_utc && (
                          <span className="text-emerald-800" data-testid="campaign-detail-end-at">
                            <span className="text-[10px] uppercase tracking-wider text-emerald-700/70 mr-1">Ends</span>
                            {formatDateLocale(campaign.end_at_utc)}
                          </span>
                        )}
                      </div>
                      {/* OT-0A.2-FE1 — patient-facing package content. */}
                      {Array.isArray(campaign.campaign_items) && campaign.campaign_items.filter(it => (it.label || '').trim()).length > 0 && (
                        <div className="pt-1" data-testid="campaign-detail-items">
                          <p className="text-[10px] uppercase tracking-wider text-emerald-700/70 mb-1">Includes</p>
                          <ul className="text-xs text-emerald-900/85 space-y-0.5 list-disc list-inside">
                            {campaign.campaign_items.filter(it => (it.label || '').trim()).map((it, idx) => (
                              <li key={idx} className={it.included === false ? 'text-emerald-900/40 line-through' : ''} data-testid={`campaign-detail-item-${idx}`}>
                                {it.label}{it.note ? <span className="text-emerald-900/60"> — {it.note}</span> : null}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {currentTreatment.differentiators?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid="treatment-differentiators">
                      {currentTreatment.differentiators.map((d, i) => <Badge key={i} variant="outline" className="text-[10px] border-slate-200">{d}</Badge>)}
                    </div>
                  )}
                  {currentTreatment.inclusions?.length > 0 && (
                    <div data-testid="treatment-inclusions">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Inclusions</p>
                      <ul className="space-y-1">{currentTreatment.inclusions.map((inc, i) => <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-emerald-400" />{inc}</li>)}</ul>
                    </div>
                  )}

                  {/* Before/After — Controlled Section (legacy treatment_showcase data) */}
                  {beforeAfter.length > 0 && (
                    <BeforeAfterSection items={beforeAfter} />
                  )}

                  {/* B2D.5 — SSOT: approved + consent-valid before/after cases */}
                  <BeforeAfterCasesSSOT clinicId={clinicId} treatmentSlug={activeTab} />

                  {/* FAQ */}
                  {currentTreatment.faq?.length > 0 && (
                    <div data-testid="faq-section">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2">FAQ</p>
                      <div className="space-y-1">
                        {currentTreatment.faq.map((f, i) => (
                          <div key={i} className="border border-slate-100 rounded-md" data-testid={`faq-item-${i}`}>
                            <button className="w-full flex items-center justify-between px-3 py-2 text-left text-xs font-medium text-[#122033]"
                              onClick={() => setExpandedFAQ(prev => ({ ...prev, [i]: !prev[i] }))}>
                              {f.q}
                              {expandedFAQ[i] ? <ChevronUp className="h-3 w-3 text-slate-400" /> : <ChevronDown className="h-3 w-3 text-slate-400" />}
                            </button>
                            {expandedFAQ[i] && <p className="px-3 pb-2 text-xs text-slate-500">{f.a}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>

      {/* Auto-opened Quote Request Modal (from Find Clinics navigation OR campaign CTA) */}
      {showQuoteModal && (
        <QuoteRequestModal
          clinicId={clinicId}
          clinicName={profile?.clinic_name || clinicId}
          preselectedTreatment={preselectedTreatment}
          preselectedCampaignId={selectedCampaignId}
          campaignSelection={selectedCampaignId ? 'explicit' : undefined}
          onClose={() => { setShowQuoteModal(false); setSelectedCampaignId(null); }}
          onSuccess={() => { setShowQuoteModal(false); setSelectedCampaignId(null); }}
        />
      )}

      {/* OT-0F.1 — Accreditation evidence drilldown modal (patient-safe metadata only) */}
      <AccreditationEvidenceModal
        open={!!accreditationModal}
        accreditation={accreditationModal}
        onClose={() => setAccreditationModal(null)}
      />

      {/* P1-1: Sticky bottom CTA — always accessible */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border-t border-slate-200 py-3 px-4 z-30" data-testid="sticky-quote-cta">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="text-sm text-slate-600 hidden sm:block">
            Interested in treatment at <span className="font-medium text-slate-800">{profile.clinic_name}</span>?
          </div>
          <QuoteRequestButton clinicId={clinicId} clinicName={profile.clinic_name || clinicId} preselectedTreatment={preselectedTreatment} />
        </div>
      </div>
    </div>
  );
}
